import { NgClass } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, model } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { StrategyDspAudiencePreset } from "@front/m19-api-client";
import {
  IHintDirective,
  IIconComponent,
  ILabelComponent,
  IRadioComponent,
  IRadioGroupComponent,
  ITooltipDirective,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { DspSmartAudiencesService } from "@m19-board/dsp/dsp-smart-audiences-service/dsp-smart-audiences.service";

@Component({
  selector: "app-dsp-smart-audience-selector",
  imports: [
    IHintDirective,
    NgClass,
    TranslocoDirective,
    IRadioComponent,
    ILabelComponent,
    IRadioGroupComponent,
    IIconComponent,
    ITooltipDirective,
  ],
  template: `<IRadioGroup
    name="smartAudience"
    class="flex max-w-3xl flex-col gap-4 rounded-lg border px-4 py-2 pb-4"
    *transloco="let t"
    [value]="selectedPreset()"
    (valueChange)="select($event)"
  >
    <div class="type-body text-muted-foreground">
      {{ t("dsp-smart-audience-selector.select_a_smart_audience_preset") }}
    </div>
    @for (preset of availablePresets(); track preset.title) {
      @let isSelected = selectedPreset() === preset.preset;
      <div
        class="flex"
        [class.cursor-not-allowed]="preset.disabled"
        [iTooltip]="preset.disabled ? t('dsp-audience-preset.amc_not_available_yet') : undefined"
      >
        <label
          class="flex grow"
          [class.cursor-pointer]="!preset.disabled"
          [class.pointer-events-none]="preset.disabled"
          [class.opacity-60]="preset.disabled"
          iLabel
          [for]="preset.preset"
          type="card"
        >
          <div class="flex gap-4">
            <div class="flex items-center text-xl" [ngClass]="isSelected ? 'text-main' : 'text-muted-foreground'">
              <IIcon [name]="preset.icon" />
            </div>
            <div>
              <div class="type-body-medium">
                {{ t("dsp-audience-preset." + preset.title) }}
              </div>
              <div class="text-muted-foreground">{{ t("dsp-audience-preset." + preset.description) }}</div>
              @if (preset.warning && isSelected) {
                <div iHint variant="warning" class="mt-2">{{ t("dsp-audience-preset." + preset.warning) }}</div>
              }
            </div>
          </div>
          @if (preset.disabled) {
            <div class="text-muted-foreground flex items-center text-xl">
              <IIcon name="lock" />
            </div>
          } @else {
            <IRadio [id]="preset.preset" [value]="preset.preset" size="lg" />
          }
        </label>
      </div>
    }
  </IRadioGroup>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspSmartAudienceSelectorComponent {
  private readonly smartAudiencesService = inject(DspSmartAudiencesService);

  private readonly amcPresetsAvailable = toSignal(this.smartAudiencesService.amcPresetsAvailable$, {
    initialValue: false,
  });

  readonly availablePresets = computed(() =>
    this.smartAudiencesService.getAvailablePresets().map((preset) => ({
      ...preset,
      disabled: !!preset.amc && !this.amcPresetsAvailable(),
    })),
  );

  readonly selectedPreset = model.required<StrategyDspAudiencePreset>();

  select(preset?: StrategyDspAudiencePreset) {
    if (!preset) {
      return;
    }
    this.selectedPreset.set(preset);
  }
}
